import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Snowflake, Mountain, Cable } from 'lucide-react';

const jahorinaImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/dsw9hrn8_image.png",
    alt: "Olimpijski krugovi na Jahorini"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/capity45_image.png",
    alt: "Ski lift i skijaši na Jahorini"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/2lc0wqu1_image.png",
    alt: "Terasa s pogledom na skijaške staze"
  }
];

const JahorinaWinter = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % jahorinaImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + jahorinaImages.length) % jahorinaImages.length);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sage-600 text-sm font-medium tracking-wider mb-2 flex items-center justify-center gap-2">
            <Snowflake size={16} />
            Zimska čarolija
            <Snowflake size={16} />
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">
            Jahorina zimi
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Carousel */}
          <div className="relative group">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              {jahorinaImages.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              
              {/* Navigation Arrows */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <ChevronLeft size={24} className="text-sage-700" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <ChevronRight size={24} className="text-sage-700" />
              </button>

              {/* Image Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {jahorinaImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex 
                        ? 'bg-white w-6' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Decorative badge */}
            <div className="absolute -top-4 -right-4 bg-sage-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg hidden md:flex items-center gap-2">
              <Mountain size={16} />
              Olimpijsko skijalište
            </div>
          </div>

          {/* Content */}
          <div>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Jahorina je jedno od najboljih skijališta u regiji, poznato po savršeno uređenim stazama i dugoj zimskoj sezoni. Smještena na idealnoj nadmorskoj visini, nudi obilje prirodnog snijega i moderne sustave zasnježivanja koji garantiraju vrhunske uvjete za skijanje.
            </p>
            
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Široke i raznolike staze prilagođene su početnicima, rekreativcima i iskusnim skijašima, dok suvremene žičare osiguravaju brz i ugodan prijevoz bez čekanja.
            </p>

            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Posebnu prednost daje apartman smješten na najboljoj mogućoj lokaciji – <span className="text-sage-700 font-semibold">odmah na skijalištu</span>, što omogućava pravi <span className="text-sage-700 font-semibold">ski-in/ski-out doživljaj</span> i maksimalnu udobnost tijekom boravka.
            </p>

            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Uz vrhunski ski doživljaj, bogatu gastronomsku ponudu i živahnu après-ski atmosferu, Jahorina je destinacija koju morate doživjeti.
            </p>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Snowflake, label: 'Prirodni snijeg', value: '150+ dana' },
                { icon: Mountain, label: 'Staze', value: '25+ km' },
                { icon: Cable, label: 'Žičare', value: '8 modernih' },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-4 shadow-md text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <feature.icon size={24} className="text-sage-600 mx-auto mb-2" />
                  <p className="text-sage-700 font-bold text-lg">{feature.value}</p>
                  <p className="text-gray-500 text-xs">{feature.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ski Map */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-serif font-bold text-gray-900">Mapa ski staza</h3>
            <p className="text-gray-500 mt-2">Otkrijte sve staze i žičare na Jahorini</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/hamzqikz_image.png"
              alt="Mapa ski staza Jahorina"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Jahorina Summer Section */}
        <div className="mt-24">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sage-600 text-sm font-medium tracking-wider mb-2 flex items-center justify-center gap-2">
              ☀️ Ljetna avantura ☀️
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">
              Jahorina ljeti
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Jahorina je ljeti prava oaza mira i svježine, idealna za bijeg od gradskih vrućina. Planina nudi prekrasne pejzaže, guste šume i brojne staze za šetnju, planinarenje i vožnju biciklom.
              </p>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Ljubitelji adrenalina mogu uživati u <span className="text-sage-700 font-semibold">zip-lineu</span>, <span className="text-sage-700 font-semibold">panoramskoj vožnji žičarom</span> i raznim outdoor aktivnostima.
              </p>

              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Uz čisti planinski zrak i netaknutu prirodu, Jahorina je savršena destinacija za opuštanje i aktivan odmor. Bilo da tražite avanturu ili potpuni mir, Jahorina ljeti pruža nezaboravno iskustvo.
              </p>

              {/* Summer Activities Icons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { emoji: '🚴', label: 'Biciklizam' },
                  { emoji: '🥾', label: 'Planinarenje' },
                  { emoji: '🎢', label: 'Alpine Coaster' },
                  { emoji: '🌲', label: 'Avantura park' },
                ].map((activity, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-4 shadow-md text-center hover:shadow-lg transition-shadow duration-300"
                  >
                    <span className="text-3xl mb-2 block">{activity.emoji}</span>
                    <p className="text-gray-600 text-sm font-medium">{activity.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Summer Images Grid */}
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-4">
              <div className="col-span-2 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/31n6z8wr_image.png"
                  alt="Mountain biking na Jahorini"
                  className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/j9aalg7s_image.png"
                  alt="Avantura park Jahorina"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_6259e2cb-c3fd-4e11-ac8c-113022f54b90/artifacts/711iyixd_image.png"
                  alt="Alpine Coaster Jahorina"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JahorinaWinter;
